/* eslint-disable no-nested-ternary */
import { LoadingOverlayController } from '@application/components/controllers';
import {
  JOIN_GROOMER_ROUTES,
  JOIN_PRO_ROUTES,
  JOIN_VET_ROUTES,
  PRO_ROUTES,
} from '@application/constants';
// import { startSessionReplayWithAccount } from '@application/datadog';
import { ENV, environmentVariable, isProduction } from '@application/env';
import { i18n } from '@application/i18n';
import { useLoadingOverlayState } from '@application/state';
import { useRouter } from '@application/utils';
import { ThemeProvider } from '@emotion/react';
import { LoadingOverlay, ToastCloseButton } from '@presentation/components';
import { GlobalStyles, lightTheme } from '@presentation/styles';
import { GoogleOAuthProvider } from '@react-oauth/google';
import dayjs from 'dayjs';
import 'dayjs/locale/fr';
import 'dayjs/locale/nl';
import 'dayjs/locale/de';
import calendar from 'dayjs/plugin/calendar';
import duration from 'dayjs/plugin/duration';
import minMax from 'dayjs/plugin/minMax';
import relativeTime from 'dayjs/plugin/relativeTime';
import utc from 'dayjs/plugin/utc';
import { useEffect } from 'react';
import { Helmet } from 'react-helmet-async';
import { useLocation } from 'react-router';
import { Flip, ToastContainer } from 'react-toastify';
import defaultImagePro from '@assets/default-image-pro.webp';
import defaultImage from '@assets/default-image.webp';
import favicon from '@assets/favicon.ico';
import { LayoutWrapper } from './LayoutWrapper';
import './icons';
import { AppRoutes } from './routes/AppRoutes';

// # CSS Imports #
import 'presentation/styles/css-reset.css';
import 'react-lazy-load-image-component/src/effects/blur.css';
import 'react-toastify/dist/ReactToastify.css';
import 'simplebar/dist/simplebar.min.css';
import 'slick-carousel/slick/slick-theme.css';
import 'slick-carousel/slick/slick.css';
import { Modals } from './Modals';

dayjs.extend(duration);
dayjs.extend(relativeTime);
dayjs.extend(minMax);
dayjs.extend(utc);
dayjs.extend(calendar);

const ALL_PRO_ROUTES = [
  ...PRO_ROUTES,
  ...JOIN_PRO_ROUTES,
  ...JOIN_VET_ROUTES,
  ...JOIN_GROOMER_ROUTES,
];

export const App = () => {
  const { language, country, infoFromCurrentPath } = useRouter();
  dayjs.locale(language);

  const { pathname } = useLocation();

  useEffect(() => {
    if (i18n.language !== language) {
      i18n.changeLanguage(language);
    }
  }, [language]);

  useEffect(() => {
    if (infoFromCurrentPath.route === 'quiz' && infoFromCurrentPath.params.length > 0) return;
    window?.scrollTo(0, 0);
  }, [infoFromCurrentPath.params.length, infoFromCurrentPath.route, pathname]);

  const { isLoading } = useLoadingOverlayState();

  const defaultOgImage = ALL_PRO_ROUTES.includes(infoFromCurrentPath.route)
    ? defaultImagePro
    : defaultImage;

  return (
    <>
      <Helmet>
        <html lang={`${language}-${country}`} />
        <title>Tipaw</title>
        <meta name="google" content="notranslate" />
        <meta property="og:type" content="site" />
        <meta property="og:site_name" content="Tipaw" />
        <meta property="og:url" content="https://tipaw.com" />
        <meta property="og:image" content={defaultOgImage} data-rh="true" />

        <meta charSet="utf-8" />
        <meta name="viewport" content="width=device-width, initial-scale=1" />
        <link rel="icon" type="image/x-icon" href={favicon} sizes="48x48" />

        {!isProduction() && <meta name="robots" content="noindex" />}
        {!isProduction() && <meta name="googlebot" content="noindex" />}
      </Helmet>
      <GoogleOAuthProvider clientId={environmentVariable(ENV.GOOGLE_ID)}>
        <ThemeProvider theme={lightTheme}>
          <LoadingOverlay isLoading={isLoading} transparent />
          <GlobalStyles />
          <LoadingOverlayController />
          <ToastContainer
            position="top-center"
            autoClose={4000}
            hideProgressBar
            newestOnTop={false}
            closeOnClick
            rtl={false}
            draggable
            transition={Flip}
            pauseOnHover
            pauseOnFocusLoss={false}
            icon={false}
            closeButton={(props) => <ToastCloseButton {...props} />}
          />

          <LayoutWrapper>
            <Modals />
            <AppRoutes />
          </LayoutWrapper>
        </ThemeProvider>
      </GoogleOAuthProvider>
    </>
  );
};
